import { useLazyQuery } from '@apollo/client/react';
import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import ComponentExportTable from './ComponentExportTable';
import {
  Brand,
  ComponentWeightBuffer,
  Country,
  FoodComponent,
  Kitchen,
  ListFoodComponentsForPrintV2Query,
  ListFoodComponentsForPrintV2QueryVariables,
  SingleFoodComponent
} from '../../../libs';
import { LIST_FOOD_COMPONENTS_FOR_PRINT_V2_QUERY } from '../../../libs/graphQL';
import { ExtendedFoodComponentForPrint } from '../../../libs/interfaces';
import { getFoodComponentStatusAttributes } from '../../../libs/utils';

interface BulkExportProps {
  components: FoodComponent[];
  kitchen: Kitchen;
  date: string;
  buffers: ComponentWeightBuffer[];
  selectedStation?: string;
  isDownloadDisabled?: boolean;
}

const buildComponentBufferMap = (
  components: FoodComponent[],
  selectedStation?: string
): Map<string, { shouldApply: boolean; totalCookedWeight: number; totalRawWeight: number }> => {
  const bufferMap = new Map<string, { shouldApply: boolean; totalCookedWeight: number; totalRawWeight: number }>();

  for (const comp of components) {
    let shouldApply = true;

    if (selectedStation) {
      let cookingStations = (comp.cookingStations ?? []).filter(Boolean);

      if (cookingStations.length > 0 && cookingStations[cookingStations.length - 1].name === 'portioning') {
        cookingStations = cookingStations.slice(0, cookingStations.length - 1);
      }

      const currentStation = cookingStations.find((s) => s.name === selectedStation);
      if (currentStation) {
        const statusAttributes = getFoodComponentStatusAttributes(cookingStations, selectedStation);
        const isDull = !statusAttributes.isCheckable && !currentStation?.endTime;
        shouldApply = !isDull;
      } else {
        shouldApply = false;
      }
    }

    const idParts = comp.id.split('#');
    const componentUuid = idParts.length > 1 ? idParts[1] : comp.id;

    const totalCookedWeight =
      comp.currentCookedWeight?.hourlyData?.[comp.currentCookedWeight?.hourlyData?.length - 1]?.weight ||
      comp.totalCookedWeight ||
      0;
    const totalRawWeight = totalCookedWeight * (comp.cookedRawFactor || 1);

    bufferMap.set(componentUuid, {
      shouldApply,
      totalCookedWeight,
      totalRawWeight
    });
  }

  return bufferMap;
};

const extendComponentsWithBufferInfo = (
  fetchedComponents: Partial<SingleFoodComponent>[],
  bufferMap: Map<string, { shouldApply: boolean; totalCookedWeight: number; totalRawWeight: number }>
): ExtendedFoodComponentForPrint[] => {
  return fetchedComponents.map((comp) => {
    const bufferData = bufferMap.get(comp.id ?? '');
    const componentWeight = comp.weight ?? 0;
    const cookedRawFactor = comp.cookedRawFactor ?? 1;

    return {
      ...comp,
      brand: comp.brand ?? Brand.CALO,
      country: comp.country ?? Country.AE,
      weight: componentWeight,
      cookedRawFactor,
      ingredients: (comp.ingredients ?? []).filter(Boolean),
      method: (comp.method ?? []).filter(Boolean),
      shouldApplyBuffer: bufferData?.shouldApply ?? true,
      totalCookedWeight: bufferData?.totalCookedWeight ?? componentWeight,
      totalRawWeight: bufferData?.totalRawWeight ?? componentWeight * cookedRawFactor
    } as ExtendedFoodComponentForPrint;
  });
};

const createPrintStyles = (): HTMLStyleElement => {
  const styleEl = document.createElement('style');
  styleEl.id = 'print-only-style';
  styleEl.textContent = `
    @media screen {
      #print-clone { display: none !important; }
    }
    @media print {
      @page {
        margin: 5mm;
        size: A4;
      }

      /* Hide everything in the document */
      html > body { visibility: hidden !important; }
      html > body > * { display: none !important; }
      
      /* Show and position the print clone */
      #print-clone {
        display: block !important;
        visibility: visible !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        z-index: 999999 !important;
        background: white !important;
      }

      #print-clone * {
        visibility: visible !important;
      }

      /* Force each component to its own page using height-based pagination */
      #print-clone .component-print-wrapper {
        display: block !important;
        width: 100% !important;
        box-sizing: border-box !important;
        min-height: 100vh !important;
        overflow: hidden !important;
        page-break-after: always !important;
        page-break-inside: avoid !important;
      }

      #print-clone .component-print-wrapper:last-child {
        page-break-after: auto !important;
        min-height: auto !important;
      }

      #print-clone .MuiTableContainer-root {
        box-shadow: none !important;
        overflow: visible !important;
      }
    }
  `;
  return styleEl;
};

const cleanupExistingElements = (): void => {
  // Remove any existing print elements
  const existingClone = document.getElementById('print-clone');
  const existingStyle = document.getElementById('print-only-style');
  existingClone?.remove();
  existingStyle?.remove();
};

const prepareCloneForPrint = (tableRef: React.RefObject<HTMLDivElement | null>): void => {
  if (!tableRef.current) return;

  const htmlTableContent = tableRef.current.cloneNode(true) as HTMLElement;
  htmlTableContent.id = 'print-clone';
  htmlTableContent.style.display = 'block';

  // Set display block on all hidden elements
  const allElements = htmlTableContent.querySelectorAll('*');
  for (const el of allElements) {
    if ((el as HTMLElement).style.display === 'none') {
      (el as HTMLElement).style.display = 'block';
    }
  }

  // Apply page-break styles to wrappers for Safari compatibility
  const wrappers = htmlTableContent.querySelectorAll('.component-print-wrapper');
  const wrappersArray = [...wrappers];
  for (const [index, wrapper] of wrappersArray.entries()) {
    const el = wrapper as HTMLElement;
    el.style.display = 'block';
    el.style.width = '100%';
    el.style.boxSizing = 'border-box';
    el.style.minHeight = '100vh';
    el.style.overflow = 'hidden';
    el.style.pageBreakAfter = index < wrappersArray.length - 1 ? 'always' : 'auto';
    el.style.pageBreakInside = 'avoid';
  }

  // Append to body for Safari
  document.body.appendChild(htmlTableContent);
};

const setupPrintCleanup = (): void => {
  const cleanup = () => {
    cleanupExistingElements();
    globalThis.removeEventListener('afterprint', cleanup);
  };

  globalThis.addEventListener('afterprint', cleanup);
  // Longer timeout for Safari which may take longer to complete the print dialog
  setTimeout(cleanup, 3000);
};

const Export = ({ components, isDownloadDisabled = false, date, kitchen, buffers, selectedStation }: BulkExportProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [fullComponents, setFullComponents] = useState<ExtendedFoodComponentForPrint[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const [fetchComponents] = useLazyQuery<ListFoodComponentsForPrintV2Query, ListFoodComponentsForPrintV2QueryVariables>(
    LIST_FOOD_COMPONENTS_FOR_PRINT_V2_QUERY
  );

  useEffect(() => {
    let cancelled = false;

    const fetchFullComponents = async () => {
      if (components.length === 0) {
        setFullComponents([]);
        return;
      }

      setIsFetching(true);

      try {
        const componentBufferMap = buildComponentBufferMap(components, selectedStation);

        const componentIds = components.map((comp) => {
          const idParts = comp.id.split('#');
          return idParts.length > 1 ? idParts[1] : comp.id;
        });

        const result = await fetchComponents({
          variables: {
            componentIds,
            kitchen,
            date
          }
        });

        if (cancelled) {
          return;
        }

        const fetchedComponents = (result.data?.listFoodComponentsForPrintV2?.data ?? []).filter(Boolean);
        const componentsWithBufferInfo = extendComponentsWithBufferInfo(fetchedComponents, componentBufferMap);

        setFullComponents(componentsWithBufferInfo);
      } catch (error) {
        if (cancelled) {
          return;
        }

        const errorMessage =
          error instanceof Error
            ? `Failed to load print data: ${error.message}`
            : 'Failed to load component details for printing. Please try again.';

        toast.error(errorMessage);
        setFullComponents([]);
      } finally {
        if (!cancelled) {
          setIsFetching(false);
        }
      }
    };

    void fetchFullComponents();

    return () => {
      cancelled = true;
    };
  }, [components, fetchComponents, kitchen, date, selectedStation]);

  const onExport = useCallback(async () => {
    if (components.length === 0) {
      toast.error('No components selected for printing');
      return;
    }

    if (isFetching) {
      toast.info('Loading component data, please wait...');
      return;
    }

    if (fullComponents.length === 0) {
      toast.error('Component data not ready. Please try again.');
      return;
    }

    // Always cleanup first to ensure fresh state
    cleanupExistingElements();

    // Small delay to ensure cleanup is complete before re-creating
    await new Promise((resolve) => setTimeout(resolve, 50));

    const styleEl = createPrintStyles();
    document.head.appendChild(styleEl);

    prepareCloneForPrint(tableRef);

    // Another small delay to ensure DOM is updated before printing
    await new Promise((resolve) => setTimeout(resolve, 50));

    setupPrintCleanup();
    globalThis.print();
  }, [fullComponents, components, isFetching]);

  return (
    <>
      <Button
        loading={isFetching}
        id="export-button"
        aria-controls={'export-button'}
        aria-haspopup="true"
        onClick={() => void onExport()}
        variant="contained"
        disabled={isDownloadDisabled || components.length === 0 || isFetching || fullComponents.length === 0}
        sx={{
          color: '#515050',
          backgroundColor: '#E6F3EC',
          fontSize: '14px',
          fontWeight: 600,
          padding: '8px 16px',
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            color: '#515050',
            backgroundColor: '#E6F3EC',
            boxShadow: 'none'
          },
          '&:disabled': {
            color: '#515050',
            backgroundColor: '#E6F3EC',
            opacity: 0.6
          }
        }}
      >
        Print{components.length > 0 ? ` (${components.length})` : ''}
      </Button>

      {fullComponents.length > 0 && (
        <Box ref={tableRef} sx={{ display: 'none' }}>
          {fullComponents.map((component, index) => (
            <div
              key={component.id}
              className="component-print-wrapper"
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                minHeight: '100vh',
                overflow: 'hidden',
                pageBreakAfter: index < fullComponents.length - 1 ? 'always' : 'auto',
                pageBreakInside: 'avoid'
              }}
            >
              <ComponentExportTable component={component} date={date} kitchen={kitchen} buffers={buffers} />
            </div>
          ))}
        </Box>
      )}
    </>
  );
};

export default Export;
